import type { ReactNode } from 'react';
import type { Ecran, QuestionBeta, Session } from '../api/contrat.js';
import { CadreGuide } from '../features/mode-guide/CadreGuide.js';
import { PageConnexion, type ErreursConnexion, type EtapeConnexion, type SaisieConnexion } from './connexion.js';
import { REGISTRE, type RegistreEcrans } from './ecrans.js';
import { Coquille, type ServicesCoquille } from './layouts.js';
import { PageLienMagique, type EtatLien } from './lien-magique.js';
import { FournisseurNavigation } from './navigation.js';
import { FournisseurNom, FournisseurRole } from './role-courant.js';
import type { EtatInstallation } from './pwa.js';
import { accueilPour, roleEffectif, type Layout } from './roles.js';
import { resoudre } from './routeur.js';
import { COULEURS, POLICE_SYSTEME } from './styles.js';

const rien = (): void => undefined;

export interface EtatConnexion {
  enCours: boolean;
  erreurs: ErreursConnexion;
  message: string | null;
  /** Absente : première étape (adresse et mot de passe). */
  etape?: EtapeConnexion;
}

export interface PropsApp {
  chemin: string;
  session: Session | null;
  largeur: number;
  enLigne: boolean;
  enAttente?: number;
  installation?: EtatInstallation;
  questionsBeta?: readonly QuestionBeta[];
  reporteesBeta?: readonly string[];
  saisieEnCours?: boolean;
  appelEnCours?: boolean;
  connexion?: EtatConnexion;
  lien?: EtatLien;
  /** Écrans des lots (registre) ; par défaut, celui construit depuis `features/*`. */
  registre?: RegistreEcrans;
  /** Étiquette « données d’exemple » sur les pages quand l’application tourne sur les mocks (UX-08). */
  donneesExemple?: boolean;
  onNaviguer?: (chemin: string) => void;
  onConnexion?: (saisie: SaisieConnexion) => void;
  onChangerAdresse?: () => void;
  onRevenir?: () => void;
  onDeconnexion?: () => void;
  onInstaller?: () => void;
  onRepondreBeta?: (question: QuestionBeta, option: string) => void;
  onPlusTardBeta?: (question: QuestionBeta) => void;
}

const CONNEXION_VIDE: EtatConnexion = { enCours: false, erreurs: {}, message: null };

function Redirection({ vers }: { vers: string }) {
  return (
    <p data-redirection={vers} role="status" style={{ padding: 24, fontFamily: POLICE_SYSTEME, fontSize: 18 }}>
      Redirection en cours…
    </p>
  );
}

/** Page d’un écran des maquettes tant que le lot qui le construit n’a pas branché son composant. */
function PageEcran({ ecran, donneesExemple }: { ecran: Ecran; donneesExemple: boolean }) {
  return (
    <section data-ecran={ecran.id}>
      <h1 style={{ color: COULEURS.marine, fontSize: 28 }}>{ecran.titre}</h1>
      {donneesExemple ? <p style={{ color: COULEURS.ardoise }}>Données d’exemple</p> : null}
      <p>Cet écran sera disponible dans une prochaine version de Lakou.</p>
    </section>
  );
}

function PageIntrouvable({ accueil, visiteur = false }: { accueil: string; visiteur?: boolean }) {
  return (
    <section data-page="introuvable">
      <h1 style={{ color: COULEURS.marine, fontSize: 28 }}>Page introuvable</h1>
      <p>Cette page n’existe pas ou n’est plus à cette adresse.</p>
      {visiteur ? <p>Si vous suivez votre dossier, rouvrez le lien depuis le dernier message reçu du Cabinet MAAT.</p> : null}
      <p>
        <a href={accueil} style={{ display: 'inline-flex', alignItems: 'center', minHeight: 44, color: COULEURS.marine }}>
          Retourner à l’accueil
        </a>
      </p>
    </section>
  );
}

function PageEspaceVide() {
  return (
    <section data-page="espace-vide">
      <h1 style={{ color: COULEURS.marine, fontSize: 28 }}>Votre espace</h1>
      <p>Votre espace n’est pas encore ouvert. Le Cabinet MAAT vous contactera dès qu’il le sera.</p>
    </section>
  );
}

export function App(props: PropsApp) {
  const { chemin, session, largeur, enLigne } = props;
  const naviguer = props.onNaviguer ?? rien;
  const decision = resoudre({ chemin, session, largeur });

  const services: ServicesCoquille = {
    enLigne,
    enAttente: props.enAttente ?? 0,
    installation: props.installation ?? 'indisponible',
    questionsBeta: props.questionsBeta ?? [],
    reporteesBeta: props.reporteesBeta ?? [],
    saisieEnCours: props.saisieEnCours ?? false,
    appelEnCours: props.appelEnCours ?? false,
    onInstaller: props.onInstaller ?? rien,
    onRevenir: props.onRevenir ?? rien,
    onDeconnexion: props.onDeconnexion ?? rien,
    onRepondreBeta: props.onRepondreBeta ?? rien,
    onPlusTardBeta: props.onPlusTardBeta ?? rien,
  };

  const dansCoquille = (layout: Layout, contenu: ReactNode) =>
    session ? (
      <Coquille layout={layout} session={session} chemin={chemin} largeur={largeur} services={services}>
        {contenu}
      </Coquille>
    ) : null;

  let contenu: ReactNode;
  switch (decision.type) {
    case 'rediriger':
      contenu = <Redirection vers={decision.vers} />;
      break;
    case 'connexion': {
      const c = props.connexion ?? CONNEXION_VIDE;
      contenu = (
        <PageConnexion
          onSoumettre={props.onConnexion ?? rien}
          enCours={c.enCours}
          erreurs={c.erreurs}
          message={c.message}
          {...(c.etape ? { etape: c.etape } : {})}
          onChangerAdresse={props.onChangerAdresse ?? rien}
        />
      );
      break;
    }
    case 'lien-magique':
      contenu = <PageLienMagique etat={props.lien ?? { etat: 'verification' }} />;
      break;
    case 'ecran': {
      const Composant = (props.registre ?? REGISTRE)[decision.ecran.id];
      const page = Composant ? (
        <Composant ecran={decision.ecran} />
      ) : (
        <PageEcran ecran={decision.ecran} donneesExemple={props.donneesExemple ?? false} />
      );
      // Couloir G, points 1 et 2 : choix du mode, bandeau du mode, bandeau « Démarrage guidé » d'un écran ouvert depuis le guide.
      contenu = dansCoquille(
        decision.layout,
        session ? (
          <CadreGuide session={session} chemin={chemin} largeur={largeur} ecranId={decision.ecran.id}>
            {page}
          </CadreGuide>
        ) : (
          page
        ),
      );
      break;
    }
    case 'espace-vide':
      contenu = dansCoquille(decision.layout, <PageEspaceVide />);
      break;
    case 'introuvable':
      contenu = session ? (
        dansCoquille(decision.layout, <PageIntrouvable accueil={accueilPour(roleEffectif(session), largeur)} />)
      ) : (
        // Visiteur sans session : page autonome comme la connexion, en grands caractères, signée.
        <main id="contenu" style={{ padding: 16, maxWidth: 640, margin: '0 auto', fontSize: 18, lineHeight: 1.5 }}>
          <PageIntrouvable accueil="/" visiteur />
          <p style={{ color: COULEURS.marine }}>Cabinet MAAT</p>
        </main>
      );
      break;
  }

  return (
    <FournisseurNavigation value={naviguer}>
      <FournisseurNom value={session ? (session.effectiveUser ?? session.user).displayName : null}>
        <FournisseurRole value={session ? roleEffectif(session) : null}>{contenu}</FournisseurRole>
      </FournisseurNom>
    </FournisseurNavigation>
  );
}
